import {Button} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {config} from '@/app/app.config';

import type {FC, ReactNode} from 'react';

export interface DebugGenerateBadgeProps {
  onGenerate: () => void;
}

export const DebugGenerateBadge: FC<DebugGenerateBadgeProps> = ({onGenerate}) => {
  const {t} = useTranslation('common');

  return (
    <Button
      variant="secondary"
      size="xs"
      type="button"
      onClick={onGenerate}
      data-slot="debug-generate-badge"
    >
      {t('debugTestData.generate')}
    </Button>
  );
};

/**
 * Composes an existing corner badge (e.g. the AI indicator) with the debug-only
 * generate trigger. Returns `indicator` unchanged outside debug mode, so `Field`'s
 * badge-in-label-line layout is untouched in production.
 */
export function withDebugGenerate(indicator: ReactNode, onGenerate: () => void): ReactNode {
  if (!config.debug) return indicator;

  return (
    <>
      <DebugGenerateBadge onGenerate={onGenerate} />
      {indicator}
    </>
  );
}

const DEBUG_SET_VALUE_OPTIONS = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouch: true
} as const;

/**
 * `form.setValue`'s signature is generic per-form (`UseFormSetValue<TFieldValues>`), which
 * TS won't assign into a plain function parameter — cast `form.setValue as DebugSetValue`
 * once per component and pass the result to `withDebugSetValue`.
 */
export type DebugSetValue = (
  field: string,
  value: string,
  options?: typeof DEBUG_SET_VALUE_OPTIONS
) => void;

/**
 * `withDebugGenerate` for a `register()`-bound field — writes the generated value via
 * `form.setValue` and re-fires the field's own change side-effect (e.g. dismissing an AI badge).
 */
export function withDebugSetValue(
  indicator: ReactNode,
  setValue: DebugSetValue,
  field: string,
  generate: () => string,
  onAfterChange?: () => void
): ReactNode {
  return withDebugGenerate(indicator, () => {
    setValue(field, generate(), DEBUG_SET_VALUE_OPTIONS);
    onAfterChange?.();
  });
}

/**
 * `withDebugGenerate` for a `Controller`-bound field — writes the generated value via the
 * field's own `onChange` and re-fires the change side-effect (e.g. dismissing an AI badge).
 */
export function withDebugFieldChange(
  indicator: ReactNode,
  onChange: (value: string) => void,
  generate: () => string,
  onAfterChange?: () => void
): ReactNode {
  return withDebugGenerate(indicator, () => {
    onChange(generate());
    onAfterChange?.();
  });
}
